// src/pages/HomePage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import productService from '../services/productService';
import ProductGrid from '../components/product/ProductGrid';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import HeroCarousel from '../components/home/HeroCarousel';
import hero1 from '../assets/hero1.jpg';
import hero2 from '../assets/hero2.jpg';
import hero3 from '../assets/hero3.jpg';
import hero4 from '../assets/hero4.jpg';

/**
 * Home Page Component
 * 
 * Features:
 * - Hero section with CTA
 * - Featured products
 * - College showcase
 * - Benefits section
 */
const FEATURED_COUNT = 8;
const FEATURED_DESIGNS = ['Minimalist', 'DemonSlayer', 'KPop', 'Balidaan'];
const FEATURED_COLORS = ['Black', 'White'];
const FEATURED_COLLEGE_COUNT = 3;

// Blueprint, circuit board, workshop, late-night desk — abstract enough not to
// tie the hero to any one campus.
const HERO_SLIDES = [hero1, hero2, hero3, hero4];

// Product names are built as "{College} {Design} {Color}" by the bulk uploader.
const parseVariant = (name = '') => {
  const parts = name.trim().split(/\s+/);
  return { design: parts[parts.length - 2], color: parts[parts.length - 1] };
};

/**
 * Picks the homepage line-up: every design in both colors, spread across a few
 * colleges. The college window advances each day so the grid isn't frozen on
 * whichever college happens to be first in the catalog.
 */
const selectFeatured = (products) => {
  const byVariant = new Map();
  const colleges = [];

  products.forEach((product) => {
    const { design, color } = parseVariant(product.name);
    const collegeId = product.college?.id;
    if (!collegeId || !design || !color) return;
    if (!colleges.includes(collegeId)) colleges.push(collegeId);
    byVariant.set(`${collegeId}|${design}|${color}`, product);
  });

  if (!colleges.length) return products.slice(0, FEATURED_COUNT);

  const dayIndex = Math.floor(Date.now() / 86400000) % colleges.length;
  const rotation = Array.from(
    { length: Math.min(FEATURED_COLLEGE_COUNT, colleges.length) },
    (_, i) => colleges[(dayIndex + i) % colleges.length]
  );

  const picked = [];
  const pickedIds = new Set();

  FEATURED_COLORS.forEach((color, colorIndex) => {
    FEATURED_DESIGNS.forEach((design, designIndex) => {
      const start = colorIndex * FEATURED_DESIGNS.length + designIndex;
      // Walk the rotation so a college that lacks this variant falls to the next.
      for (let step = 0; step < rotation.length; step++) {
        const collegeId = rotation[(start + step) % rotation.length];
        const product = byVariant.get(`${collegeId}|${design}|${color}`);
        if (product && !pickedIds.has(product.id)) {
          pickedIds.add(product.id);
          picked.push(product);
          return;
        }
      }
    });
  });

  // Top up if the catalog is thinner than the design/color grid assumes.
  products.forEach((product) => {
    if (picked.length < FEATURED_COUNT && !pickedIds.has(product.id)) {
      pickedIds.add(product.id);
      picked.push(product);
    }
  });

  return picked.slice(0, FEATURED_COUNT);
};

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchFeaturedProducts();
  }, []);
  
  const fetchFeaturedProducts = async () => {
    try {
      setLoading(true);
      const data = await productService.getAllProducts();
      setFeaturedProducts(selectFeatured(data));
    } catch (error) {
      console.error('Error fetching featured products:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroCarousel slides={HERO_SLIDES}>
        <div className="text-left max-w-2xl">
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight tracking-tight text-white">
            Drip Harder, <br />
            Study Smarter.
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-100 font-medium">
            Merch for the minds that build India.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/products">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full shadow-lg transition-all hover:shadow-blue-500/50">
                Shop Collection
              </Button>
            </Link>

          </div>
        </div>
      </HeroCarousel>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Custom Name Printing</h3>
              <p className="text-gray-600">
                Personalize your merchandise with custom name printing at no extra cost
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Premium Quality</h3>
              <p className="text-gray-600">
                High-quality fabric and printing for long-lasting wear
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Fast Delivery</h3>
              <p className="text-gray-600">
                Quick dispatch and delivery across India
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Products */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Featured Products
            </h2>
            <p className="text-gray-600">
              Check out our most popular college merchandise
            </p>
          </div>
          
          {loading ? (
            <Loading message="Loading featured products..." />
          ) : (
            <>
              <ProductGrid products={featuredProducts} loading={false} />
              
              <div className="text-center mt-12">
                <Link to="/products">
                  <Button size="lg">
                    View All Products
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
      
      {/* Colleges Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              For Top Indian Colleges
            </h2>
            <p className="text-gray-600">
              Exclusive designs for premier institutions
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['IIT Delhi', 'IIT Bombay', 'IIT Madras', 'IIT Kanpur', 'IIT Kharagpur', 'IIT Roorkee', 'IIT Guwahati', 'IIT Hyderabad', 'IIT Indore', 'IIT (BHU) Varanasi', 'IIT Ropar', 'IIT Gandhinagar'].map((college) => (
              <div 
                key={college}
                className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-xl transition-shadow"
              >
                <p className="font-semibold text-gray-900">{college}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;